/* eslint-disable react/prop-types */
import React from 'react'
import { Space, Button } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { FormatedTimestamp } from '../../Shared/FormatedTimestamp'
import ProgressInput from '../ProgressInput'
import { StatusTag } from '../../Shared'
import HistoryView from './HistoryView'
import { useSmartTranslation } from '../../../cls/TranslationContext'

// Simple progress view subcomponent for cases without recurrence or subtasks
/**
 * SimpleProgressView renders a simple progress control when there are no subtasks
 * or recurrence needed. It shows the current progress status (or a completed tag),
 * the next recurrence date if any, and buttons to update progress or reset history.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.historyKey='history-drawer'] - Modal key for history drawer.
 * @param {Object} props._itm - The item object containing progress, history, and recurrence.
 * @param {Function} props._doSave - Handler to save a new progress value or reset history.
 * @param {Object} props.uiStore - MobX store for controlling UI (e.g., opening modals).
 * @param {string} [props.category='progress'] - History category to record into.
 * @param {string} [props.type='sprint'] - The type of progress tag to render.
 *
 * @returns {JSX.Element|null} The rendered simple progress view, or null if history is uninitialized.
 */
const SimpleProgressView = observer(
	({
		historyKey = 'history-drawer',
		_itm,
		_doSave,
		uiStore,
		category = 'progress',
		type = 'sprint'
	}) => {
		const { t } = useSmartTranslation()
		// Check if history is initialized for _itm
		if (!_itm.history) {
			console.warn('History not initialized for the item')
			return null
		} else {
			// console.log('History initialized for the item', toJS(_itm.history))
		}

		const recurrenceNext = _itm.recurrence?.next || false

		return (
			<>
				<p id="track-no-sub-no-rec">
					{_itm.displayName} -- {category}
				</p>
				{recurrenceNext && (
					<p>
						{t('single.next')}
						{': '}
						<FormatedTimestamp timestamp={recurrenceNext} />
					</p>
				)}
				<Space style={{ width: '100%' }}>
					{_itm.progress === 'completed' ? (
						<StatusTag
							tag="completed"
							type={type}
							style={{ marginRight: 3 }}
						/>
					) : (
						<ProgressInput
							type={type}
							value={_itm.progress}
							onChange={_doSave}
						/>
					)}
					<HistoryView
						_itm={_itm}
						historyKey={historyKey}
						uiStore={uiStore}
						category={category}
					/>
					<Button
						className="no-print"
						type="link"
						onClick={() => {
							_doSave('reset-history')
						}}
						icon={<DeleteOutlined />}
					/>
				</Space>
			</>
		)
	}
)

export default SimpleProgressView
